import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Shield, AlertTriangle, CheckCircle, RotateCcw, Calendar, ArrowRight, Loader2, Mail, Share2, ExternalLink, LogOut, Settings, ImageIcon, FileText, CreditCard, Users, Building } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { useSEO } from "../hooks/useSEO";
import { supabase } from "@/lib/supabase";
import { UsageLimitDisplay } from "../components/klartext/UsageLimitDisplay";
import { UpgradeModal } from "../components/klartext/UpgradeModal";
import { KlartextAuthModal } from "../components/klartext/KlartextAuthModal";
import { FeedbackButtons } from "../components/klartext/FeedbackButtons";
import { ImageUpload } from "../components/klartext/ImageUpload";
import { PricingSection } from "../components/klartext/PricingSection";
import { CheckHistory } from "../components/klartext/CheckHistory";
import { FamilyMemberManager } from "../components/klartext/FamilyMemberManager";
import { CompanyMemberManager } from "../components/klartext/CompanyMemberManager";
import { MonthlyReportCard } from "../components/klartext/MonthlyReportCard";
import { KlartextPlan } from "@/lib/klartext-plans";
import { initFingerprint, getFingerprint } from "@/lib/klartext-fingerprint";
import { User, Session } from "@supabase/supabase-js";

// Interface for server-side usage data
interface UsageData {
  plan: string;
  limit: number | 'unlimited';
  used: number;
  remaining: number | 'unlimited';
  canCheck: boolean;
}

type AmpelColor = "gruen" | "gelb" | "rot";

interface CheckResult {
  ampel: AmpelColor;
  ueberschrift: string;
  erklaerung: string;
  was_tun: string[];
  kategorie?: string;
}

interface SubscriptionState {
  plan: KlartextPlan;
  subscribed: boolean;
  subscriptionEnd: string | null;
}

interface MembershipInfo {
  isMember: boolean;
  memberType: 'family' | 'company' | null;
  ownerEmail: string | null;
}

const Header = ({ 
  user, 
  subscription, 
  membershipInfo,
  onLogout, 
  onManageSubscription,
  isManagingSubscription,
  onLoginClick,
}: { 
  user: User | null;
  subscription: SubscriptionState;
  membershipInfo: MembershipInfo;
  onLogout: () => void;
  onManageSubscription: () => void;
  isManagingSubscription: boolean;
  onLoginClick: () => void;
}) => (
  <header className="sticky top-0 z-50 border-b border-white/10 bg-white/80 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60 shadow-sm">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
      <Link to="/" className="flex items-center gap-3">
        <div className="relative">
          <img 
            src="https://customer-assets.emergentagent.com/job_tech-support-mvp/artifacts/88uemucq_LogoKlartech%20%281%29.png" 
            alt="Klartech Müritz Logo" 
            className="h-12 w-auto object-contain transition-transform hover:scale-105"
          />
          <div className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-green-500 animate-pulse ring-2 ring-white"></div>
        </div>
      </Link>
      
      <div className="flex items-center gap-3">
        {/* Membership badge for invited members */}
        {user && membershipInfo.isMember && (
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-full">
            {membershipInfo.memberType === 'family' ? (
              <Users className="h-4 w-4 text-blue-600" />
            ) : (
              <Building className="h-4 w-4 text-indigo-600" />
            )}
            <span className="text-xs text-slate-600">
              {membershipInfo.memberType === 'family' ? 'Familienmitglied' : 'Firmenmitglied'}
            </span>
          </div>
        )}
        
        {user && subscription.plan !== 'free' && !membershipInfo.isMember && (
          <span className="hidden sm:inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 text-sm font-semibold rounded-full">
            {subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)}
          </span>
        )}
        
        {user && (
          <Link to="/abo-zentrale">
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center gap-1"
            >
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Meine Abos</span>
            </Button>
          </Link>
        )}
        
        {user && subscription.subscribed && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onManageSubscription}
            disabled={isManagingSubscription}
            className="flex items-center justify-center"
          >
            {isManagingSubscription ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Settings className="h-4 w-4" />
            )}
          </Button>
        )}
        
        {user ? (
          <Button variant="ghost" size="sm" onClick={onLogout}>
            <LogOut className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Abmelden</span>
          </Button>
        ) : (
          <Button 
            variant="default" 
            size="sm" 
            onClick={onLoginClick}
            className="bg-gradient-to-r from-primary to-blue-600 text-white rounded-xl"
          >
            Anmelden
          </Button>
        )}
        
        <Link to="/">
          <Button variant="outline" className="rounded-xl hidden sm:flex">
            Zurück zur Startseite
          </Button>
        </Link>
      </div>
    </div>
  </header>
);

const Hero = ({ onScrollToForm }: { onScrollToForm: () => void }) => (
  <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
    <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-100/40 to-indigo-100/40 rounded-full blur-3xl animate-pulse"></div>
    <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-cyan-200/20 to-blue-200/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
    
    <div className="mx-auto w-full max-w-4xl px-6 text-center relative z-10">
      <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 px-4 py-2 text-sm font-medium text-slate-700 mb-6 shadow-sm">
        <Shield className="h-4 w-4 text-primary" />
        Kostenloser Sicherheitscheck
      </div>
      
      <h1 className="text-balance text-4xl font-black tracking-tight text-slate-900 sm:text-5xl lg:text-6xl leading-tight mb-6">
        Ist diese Mail echt –{" "}
        <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
          oder Betrug?
        </span>
      </h1>
      
      <p className="text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto mb-8">
        Ihr schneller Online-Sicherheitscheck von Klartech Müritz. 
        Text einfügen oder Screenshot hochladen – wir analysieren verdächtige Nachrichten für Sie.
      </p>
      
      <Button
        onClick={onScrollToForm}
        className="group relative rounded-xl bg-gradient-to-r from-primary to-blue-600 px-8 py-4 text-lg font-bold text-white shadow-xl transition-all hover:shadow-2xl hover:scale-105 overflow-hidden"
      >
        <span className="relative z-10 flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Klartext-Check starten
          <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
        </span>
      </Button>
    </div>
  </section>
);

const AmpelDisplay = ({ color }: { color: AmpelColor }) => {
  const configs = {
    gruen: {
      bgColor: "bg-green-500",
      icon: CheckCircle,
      label: "Sicher",
      ringColor: "ring-green-300"
    },
    gelb: {
      bgColor: "bg-yellow-500",
      icon: AlertTriangle,
      label: "Vorsicht",
      ringColor: "ring-yellow-300"
    },
    rot: {
      bgColor: "bg-red-500",
      icon: AlertTriangle,
      label: "Gefahr",
      ringColor: "ring-red-300"
    }
  };

  const config = configs[color];
  const Icon = config.icon;

  return (
    <div className="flex flex-col items-center mb-8">
      <div className={`w-32 h-32 rounded-full ${config.bgColor} flex items-center justify-center shadow-2xl ring-8 ${config.ringColor} animate-pulse`}>
        <Icon className="h-16 w-16 text-white" />
      </div>
      <span className={`mt-4 text-xl font-bold ${color === 'gruen' ? 'text-green-700' : color === 'gelb' ? 'text-yellow-700' : 'text-red-700'}`}>
        {config.label}
      </span>
    </div>
  );
};

const EmailCapture = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("klartext_email_signups")
        .insert({ email: email.trim() });

      if (error) {
        if (error.code === "23505") {
          toast({ title: "Sie sind bereits angemeldet!", description: "Diese E-Mail ist schon in unserer Liste." });
        } else {
          throw error;
        }
      } else {
        setIsSubscribed(true);
        toast({ title: "Erfolgreich angemeldet!", description: "Sie erhalten ab jetzt unsere Sicherheits-Tipps." });
      }
    } catch (error) {
      toast({ title: "Fehler", description: "Bitte versuchen Sie es später erneut.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubscribed) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
        <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
        <p className="text-green-800 font-medium">Vielen Dank! Sie erhalten bald unsere Tipps.</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="h-5 w-5 text-primary" />
        <h4 className="font-bold text-slate-800">Tägliche Sicherheits-Tipps?</h4>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Schützen Sie sich vor Betrug mit unseren kostenlosen Tipps per E-Mail.
      </p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          type="email"
          placeholder="Ihre E-Mail-Adresse"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="flex-1"
          disabled={isSubmitting}
        />
        <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90">
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anmelden"}
        </Button>
      </form>
    </div>
  );
};

const SocialShare = () => {
  const shareUrl = "https://klartech-mueritz.de/klartext-check";
  const shareText = "Neuer Klartext-Check von Klartech Müritz! Testet mal eure verdächtigen Mails auf Betrug:";

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Klartext-Check", text: shareText, url: shareUrl });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank");
    }
  };

  return (
    <Button variant="outline" onClick={handleShare} className="rounded-xl border-2">
      <Share2 className="mr-2 h-4 w-4" />
      Teilen
    </Button>
  );
};

const ResultCard = ({ 
  result, 
  originalText,
  exampleId,
  onReset 
}: { 
  result: CheckResult; 
  originalText: string;
  exampleId: string;
  onReset: () => void;
}) => {
  const bgColors = {
    gruen: "from-green-50 to-emerald-50 border-green-200",
    gelb: "from-yellow-50 to-amber-50 border-yellow-200",
    rot: "from-red-50 to-rose-50 border-red-200"
  };

  const isRot = result.ampel === "rot";

  return (
    <div className="space-y-6">
      <Card className={`bg-gradient-to-br ${bgColors[result.ampel]} border-2 shadow-xl`}>
        <CardContent className="p-8">
          <AmpelDisplay color={result.ampel} />
          
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-4">
            {result.ueberschrift}
          </h2>
          
          <p className="text-lg text-slate-700 text-center mb-8 leading-relaxed">
            {result.erklaerung}
          </p>
          
          <div className="bg-white/60 rounded-xl p-6 mb-8">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Was Sie tun sollten:
            </h3>
            <ul className="space-y-3">
              {result.was_tun.map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Feedback Buttons */}
          <div className="bg-white/80 rounded-xl p-4 mb-6 flex justify-center">
            <FeedbackButtons
              exampleId={exampleId}
              originalText={originalText}
              ampelResult={result.ampel}
              aiHeadline={result.ueberschrift}
              aiExplanation={result.erklaerung}
              kategorie={result.kategorie}
            />
          </div>

          {isRot && (
            <div className="bg-red-100 border-2 border-red-300 rounded-xl p-6 mb-6 text-center">
              <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-3" />
              <h4 className="font-bold text-red-800 text-lg mb-2">
                Betrug erkannt? Wir helfen Ihnen!
              </h4>
              <p className="text-red-700 mb-4">
                Buchen Sie einen kostenlosen Beratungstermin und wir zeigen Ihnen, wie Sie sich schützen können.
              </p>
              <Link to="/termine">
                <Button className="w-full sm:w-auto rounded-xl bg-red-600 hover:bg-red-700 px-8 py-4 text-lg font-bold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all">
                  <Calendar className="mr-2 h-5 w-5" />
                  Jetzt Termin buchen
                </Button>
              </Link>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onReset}
              variant="outline"
              className="rounded-xl border-2 px-6 py-3 font-semibold"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Nochmal prüfen
            </Button>
            
            <SocialShare />
            
            {!isRot && (
              <Link to="/termine">
                <Button className="w-full sm:w-auto rounded-xl bg-gradient-to-r from-primary to-blue-600 px-6 py-3 font-semibold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all">
                  <Calendar className="mr-2 h-4 w-4" />
                  Termin buchen
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>

      <EmailCapture />
    </div>
  );
};

const Footer = () => (
  <footer className="bg-slate-900 py-8 text-center text-slate-400">
    <div className="mx-auto max-w-7xl px-6">
      <p className="text-sm">
        © {new Date().getFullYear()} Klartech Müritz. Alle Rechte vorbehalten.
      </p>
      <div className="mt-4 flex justify-center gap-6 text-sm">
        <Link to="/impressum" className="hover:text-white transition-colors">
          Impressum
        </Link>
        <Link to="/datenschutz" className="hover:text-white transition-colors">
          Datenschutz
        </Link>
      </div>
    </div>
  </footer>
);

export default function KlartextCheck() {
  useSEO({
    title: "Klartext-Check – Ist diese Mail Betrug? | Klartech Müritz",
    description: "Prüfen Sie verdächtige E-Mails, SMS oder URLs kostenlos auf Betrugsmerkmale. Der Klartext-Check von Klartech Müritz analysiert Phishing und Fake-Nachrichten.",
  });

  const [searchParams] = useSearchParams();
  const [inputText, setInputText] = useState("");
  const [inputImage, setInputImage] = useState<string | null>(null);
  const [inputImageName, setInputImageName] = useState<string>("");
  const [inputMode, setInputMode] = useState<"text" | "image">("text");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [currentExampleId, setCurrentExampleId] = useState<string>("");
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionState>({
    plan: 'free',
    subscribed: false,
    subscriptionEnd: null,
  });
  const [usageData, setUsageData] = useState<UsageData>({
    plan: 'free',
    limit: 3,
    used: 0,
    remaining: 3,
    canCheck: true,
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);
  const [membershipInfo, setMembershipInfo] = useState<MembershipInfo>({
    isMember: false,
    memberType: null,
    ownerEmail: null,
  });
  
  const formRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Initialize fingerprint on mount
  useEffect(() => {
    initFingerprint();
  }, []);

  // Handle success/canceled from Stripe checkout and invite links
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast({
        title: 'Zahlung erfolgreich!',
        description: 'Ihr Abo ist jetzt aktiv. Vielen Dank!',
      });
      // Refresh subscription status
      checkSubscription();
    } else if (searchParams.get('canceled') === 'true') {
      toast({
        title: 'Checkout abgebrochen',
        description: 'Die Zahlung wurde nicht abgeschlossen.',
      });
    }
    
    // Handle invitation links - auto-open auth modal with pre-filled email
    const inviteType = searchParams.get('invite');
    const emailParam = searchParams.get('email');
    if (inviteType && emailParam && !user) {
      setInviteEmail(decodeURIComponent(emailParam));
      setShowAuthModal(true);
      // Store invite type for later acceptance
      sessionStorage.setItem('pendingInvite', JSON.stringify({ type: inviteType, email: emailParam }));
    }
  }, [searchParams, user]);

  // Auth state listener
  useEffect(() => {
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setTimeout(() => {
            checkSubscription();
            checkMembershipStatus(session.user.id);
          }, 0);
        } else {
          setSubscription({ plan: 'free', subscribed: false, subscriptionEnd: null });
          setMembershipInfo({ isMember: false, memberType: null, ownerEmail: null });
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        checkSubscription();
        checkMembershipStatus(session.user.id);
      }
    });

    return () => authSubscription.unsubscribe();
  }, []);

  const checkMembershipStatus = async (userId: string) => {
    try {
      // Check if user is an invited family member
      const { data: familyMember } = await supabase
        .from("klartext_family_members")
        .select("owner_id")
        .eq("member_user_id", userId)
        .eq("status", "accepted")
        .maybeSingle();

      if (familyMember) {
        const { data: ownerProfile } = await supabase
          .from("klartext_profiles")
          .select("email")
          .eq("id", familyMember.owner_id)
          .maybeSingle();
        
        setMembershipInfo({
          isMember: true,
          memberType: 'family',
          ownerEmail: ownerProfile?.email || null,
        });
        return;
      }

      // Check if user is an invited company member
      const { data: companyMember } = await supabase
        .from("klartext_company_members")
        .select("owner_id")
        .eq("member_user_id", userId)
        .eq("status", "accepted")
        .maybeSingle();

      if (companyMember) {
        const { data: ownerProfile } = await supabase
          .from("klartext_profiles")
          .select("email")
          .eq("id", companyMember.owner_id)
          .maybeSingle();
        
        setMembershipInfo({
          isMember: true,
          memberType: 'company',
          ownerEmail: ownerProfile?.email || null,
        });
        return;
      }

      // Not an invited member
      setMembershipInfo({ isMember: false, memberType: null, ownerEmail: null });
    } catch (error) {
      console.error("Error checking membership status:", error);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('klartext-check-subscription');
      
      if (error) {
        console.error('Subscription check error:', error);
        return;
      }

      if (data) {
        setSubscription({
          plan: data.plan || 'free',
          subscribed: data.subscribed || false,
          subscriptionEnd: data.subscription_end || null,
        });
      }
    } catch (error) {
      console.error('Failed to check subscription:', error);
    }
  };

  // Fetch usage from server
  const fetchUsage = useCallback(async () => {
    try {
      const fingerprint = getFingerprint();
      const headers: Record<string, string> = {};
      if (fingerprint) {
        headers['x-client-fingerprint'] = fingerprint;
      }

      const { data, error } = await supabase.functions.invoke('klartext-get-usage', {
        headers,
      });

      if (error) {
        console.error('Usage fetch error:', error);
        return;
      }

      if (data) {
        setUsageData({
          plan: data.plan || 'free',
          limit: data.limit,
          used: data.used,
          remaining: data.remaining,
          canCheck: data.canCheck,
        });
      }
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  }, []);

  // Fetch usage on mount and when auth changes
  useEffect(() => {
    fetchUsage();
  }, [user, fetchUsage]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({ title: 'Abgemeldet' });
  };

  const handleManageSubscription = async () => {
    setIsManagingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke('klartext-customer-portal');
      
      if (error) throw new Error(error.message);
      if (data.error) throw new Error(data.error);
      
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message || 'Portal konnte nicht geöffnet werden.',
        variant: 'destructive',
      });
    } finally {
      setIsManagingSubscription(false);
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const canCheck = (): boolean => {
    // Use server-side usage data
    return usageData.canCheck;
  };

  const handleCheck = async () => {
    // Validate input based on mode
    if (inputMode === "text" && !inputText.trim()) {
      toast({
        title: "Text fehlt",
        description: "Bitte fügen Sie den zu prüfenden Text ein.",
        variant: "destructive",
      });
      return;
    }

    if (inputMode === "image" && !inputImage) {
      toast({
        title: "Bild fehlt",
        description: "Bitte laden Sie ein Bild zum Prüfen hoch.",
        variant: "destructive",
      });
      return;
    }

    // Check usage limits
    if (!canCheck()) {
      if (!user) {
        setShowAuthModal(true);
      } else {
        setShowUpgradeModal(true);
      }
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      // Build request body based on input mode
      const requestBody = inputMode === "image" 
        ? { image: inputImage }
        : { text: inputText };

      // Get fingerprint for anonymous tracking
      const fingerprint = getFingerprint();
      const headers: Record<string, string> = {};
      if (fingerprint) {
        headers['x-client-fingerprint'] = fingerprint;
      }

      const { data, error } = await supabase.functions.invoke("klartext-check", {
        body: requestBody,
        headers,
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.error) {
        // Check if it's a limit error
        if (data.limitReached) {
          setUsageData(prev => ({ ...prev, remaining: 0, canCheck: false }));
          if (!user) {
            setShowAuthModal(true);
          } else {
            setShowUpgradeModal(true);
          }
          return;
        }
        throw new Error(data.error);
      }

      setResult(data);
      
      // Generate unique ID for this check (for feedback tracking)
      const exampleId = crypto.randomUUID();
      setCurrentExampleId(exampleId);

      // Update usage from response if available
      if (data.usage) {
        setUsageData(prev => ({
          ...prev,
          remaining: data.usage.remaining,
          used: typeof prev.limit === 'number' ? prev.limit - data.usage.remaining : 0,
          canCheck: data.usage.remaining !== 0 && data.usage.remaining !== '0',
        }));
      }

      // Track analytics
      const sessionId = sessionStorage.getItem("klartext_session") || crypto.randomUUID();
      sessionStorage.setItem("klartext_session", sessionId);
      
      supabase
        .from("klartext_check_analytics")
        .insert({
          ampel_result: data.ampel,
          input_length: inputText.length,
          session_id: sessionId,
        })
        .then(() => {});

    } catch (error: unknown) {
      console.error("Check failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Bitte versuchen Sie es später erneut.";
      toast({
        title: "Fehler bei der Analyse",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setInputText("");
    setInputImage(null);
    setInputImageName("");
    setResult(null);
    scrollToForm();
  };

  const handleImageSelected = (base64: string, fileName: string) => {
    setInputImage(base64);
    setInputImageName(fileName);
  };

  const handleAuthSuccess = async () => {
    // Check if there's a pending invitation to accept
    const pendingInviteStr = sessionStorage.getItem('pendingInvite');
    if (pendingInviteStr) {
      try {
        const pendingInvite = JSON.parse(pendingInviteStr);
        sessionStorage.removeItem('pendingInvite');
        
        // Accept the invitation via Edge Function
        const { error } = await supabase.functions.invoke('klartext-accept-invite', {
          body: { 
            inviteType: pendingInvite.type,
            email: decodeURIComponent(pendingInvite.email)
          }
        });
        
        if (error) {
          console.error('Failed to accept invitation:', error);
        } else {
          toast({
            title: 'Einladung angenommen!',
            description: 'Sie wurden erfolgreich hinzugefügt.',
          });
        }
      } catch (e) {
        console.error('Error processing invitation:', e);
      }
    }
    
    // After auth, check subscription
    checkSubscription();
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header 
        user={user}
        subscription={subscription}
        membershipInfo={membershipInfo}
        onLogout={handleLogout}
        onManageSubscription={handleManageSubscription}
        isManagingSubscription={isManagingSubscription}
        onLoginClick={() => setShowAuthModal(true)}
      />
      
      <main className="flex-1">
        <Hero onScrollToForm={scrollToForm} />
        
        <section ref={formRef} id="check-form" className="py-16 md:py-24 bg-white">
          <div className="mx-auto w-full max-w-3xl px-6">
            {!result ? (
              <Card className="shadow-xl border-slate-200">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
                    Nachricht zum Prüfen
                  </h2>
                  
                  {/* Usage Limit Display */}
                  <div className="mb-6">
                    <UsageLimitDisplay 
                      plan={subscription.plan}
                      usageData={usageData}
                      onUpgradeClick={() => user ? setShowUpgradeModal(true) : setShowAuthModal(true)}
                    />
                  </div>
                  
                  {/* Input Mode Tabs */}
                  <Tabs 
                    value={inputMode} 
                    onValueChange={(v) => setInputMode(v as "text" | "image")}
                    className="mb-6"
                  >
                    <TabsList className="grid w-full grid-cols-2 mb-4">
                      <TabsTrigger value="text" className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Text einfügen
                      </TabsTrigger>
                      <TabsTrigger value="image" className="flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" />
                        Bild hochladen
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="text" className="mt-0">
                      <Textarea
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder="E-Mail, SMS oder URL hier einfügen..."
                        className="min-h-[200px] text-base resize-none"
                        disabled={isLoading}
                      />
                    </TabsContent>
                    
                    <TabsContent value="image" className="mt-0">
                      <ImageUpload 
                        onImageSelected={handleImageSelected}
                        isLoading={isLoading}
                      />
                    </TabsContent>
                  </Tabs>
                  
                  <Button
                    onClick={handleCheck}
                    disabled={isLoading || (inputMode === "text" ? !inputText.trim() : !inputImage)}
                    className="w-full rounded-xl bg-gradient-to-r from-primary to-blue-600 py-4 text-lg font-bold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Wird analysiert...
                      </>
                    ) : (
                      <>
                        <Shield className="mr-2 h-5 w-5" />
                        Check starten
                      </>
                    )}
                  </Button>
                  
                  <p className="text-sm text-slate-500 text-center mt-4">
                    Ihre Eingabe wird sicher verarbeitet und nicht gespeichert.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <ResultCard 
                result={result} 
                originalText={inputMode === "text" ? inputText : `[Bild: ${inputImageName}]`}
                exampleId={currentExampleId}
                onReset={handleReset} 
              />
            )}
          </div>
          
          {/* Premium Features Section */}
          {user && subscription.plan !== 'free' && (
            <>
              <CheckHistory 
                userId={user.id} 
                isVisible={subscription.plan !== 'free'}
              />
              <FamilyMemberManager 
                userId={user.id}
                plan={subscription.plan}
                isVisible={subscription.plan === 'familie'}
              />
              <CompanyMemberManager 
                userId={user.id}
                plan={subscription.plan}
                isVisible={subscription.plan === 'firma'}
              />
              <MonthlyReportCard 
                userId={user.id}
                plan={subscription.plan}
                isVisible={subscription.plan === 'firma'}
              />
            </>
          )}
        </section>

        {/* Pricing Section */}
        <PricingSection 
          currentPlan={subscription.plan}
          isAuthenticated={!!user}
          onLoginRequired={() => setShowAuthModal(true)}
        />
      </main>
      
      <Footer />

      {/* Modals */}
      <UpgradeModal 
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        onLoginClick={() => {
          setShowUpgradeModal(false);
          setShowAuthModal(true);
        }}
        isAuthenticated={!!user}
      />
      
      <KlartextAuthModal
        open={showAuthModal}
        onOpenChange={(open) => {
          setShowAuthModal(open);
          if (!open) setInviteEmail(''); // Clear invite email when closing
        }}
        onSuccess={handleAuthSuccess}
        initialEmail={inviteEmail}
      />
    </div>
  );
}
